import React, { useState, useEffect } from 'react';
import { FiCreditCard, FiAlertCircle } from 'react-icons/fi';
import { KPICard as UIKPICard, LoadingSpinner } from '../../ui';
import { useExecutiveCreditUsageRate } from '../../../hooks/useExecutiveKPIs';

import { KPIProps } from '../../../types/kpi';
const CreditCardIcon = FiCreditCard as React.ElementType;
const AlertCircleIcon = FiAlertCircle as React.ElementType;

const CreditUsageRateKPI: React.FC<KPIProps> = ({
  branchId = 0,
  timeRange,
  onError,
  showLoading = true,
  compact = false,
  className = ''
}) => {
  const [animatedValue, setAnimatedValue] = useState<number>(0);
  
  // Fetch credit usage data
  const { data, isLoading, error } = useExecutiveCreditUsageRate(branchId, timeRange as string);
  
  // Extract data
  const creditUsageRate = data?.creditUsageRate || 0;
  
  // Handle error
  React.useEffect(() => {
    if (error && onError) {
      onError(error as Error);
    }
  }, [error, onError]);
  
  // Animation for numbers
  useEffect(() => {
    if (!creditUsageRate && creditUsageRate !== 0) return;
    
    const endValue = creditUsageRate;
    const startValue = animatedValue || 0;

    const animateValue = (start: number, end: number, duration: number) => {
      if (start === end) {
        setAnimatedValue(end);
        return;
      }
      
      let startTimestamp: number | null = null;
      const step = (timestamp: number) => {
        if (!startTimestamp) startTimestamp = timestamp;
        const progress = Math.min((timestamp - startTimestamp) / duration, 1);
        const value = progress * (end - start) + start;
        setAnimatedValue(value);
        if (progress < 1) {
          window.requestAnimationFrame(step);
        }
      };
      window.requestAnimationFrame(step);
    };

    const timerId = setTimeout(() => {
      animateValue(startValue, endValue, 1000);
    }, 100);
    
    return () => clearTimeout(timerId);
  }, [creditUsageRate]);
  
  if (isLoading && showLoading) {
    return (
      <div className={`animate-enter animate-delay-1 ${className}`}>
        <div className="card-base card-padding">
          <div className="flex items-center justify-center h-32">
            <LoadingSpinner variant="minimal" size="md" />
          </div>
        </div>
      </div>
    );
  }
  
  const showWarning = creditUsageRate < 15;
  
  return (
    <UIKPICard
      title="متوسط نرخ مصرف"
      value={`${animatedValue.toFixed(1)}%`}
      variant="success"
      accentStyle="corner"
      className={`animate-enter ${className}`}
      icon={showWarning ? <AlertCircleIcon className="intent-warning" size={18} aria-label="هشدار" /> : <CreditCardIcon size={20} />}
    />
  );
};

export default CreditUsageRateKPI;
